import React, { useState, useEffect, useMemo, useRef } from "react";
import { CSVLink } from "react-csv";
import API from "../../services/api";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import MultiSelectFilter from "../../shared/MultiSelectFilter";
import { BrandsTypes, formatSpentValue } from "../../utils/constants";

const formatLocationLabel = (location = "") =>
  String(location)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getMonthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const formatMonthHeader = (date) => {
  const d = new Date(date);
  const month = d.toLocaleString("default", { month: "short" });
  const year = String(d.getFullYear()).slice(-2);
  return `${month}'${year}`;
};

function LocationMonthWiseReport({ dateRange, initiateDownloadCSV, setInitiateDownloadCSV, selectedSalesPersonId }) {
  const [locationData, setLocationData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const fetchData = async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      setLocationData([]);
      return;
    }
    try {
      setLoader(true);
      const response = await API({
        url: "/alpha/get-location-date-month-wise-reports",
        method: "POST",
        responseType: "json",
        data: { dateRange, isMonthWise: true, sales_person_id: +selectedSalesPersonId || undefined },
      });
      if (response?.code === 200) {
        setLocationData(Array.isArray(response.data) ? response.data : []);
      } else {
        setLocationData([]);
      }
    } catch (error) {
      console.error("Error fetching location data", error);
      setLocationData([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedSalesPersonId]);

  const monthColumns = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return [];
    const start = new Date(dateRange[0]);
    const end = new Date(dateRange[1]);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    end.setDate(1);
    end.setHours(0, 0, 0, 0);
    const months = [];
    for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
      months.push(new Date(d));
    }
    return months;
  }, [dateRange]);

  const locationRows = useMemo(() => {
    const filtered = (locationData || [])
    let unique = [...new Set(filtered.map((r) => `${r.location} - ${BrandsTypes.find((b) => b.value === r.new_user_type)?.label || ""}`).filter(Boolean))];
    unique = unique.sort((a, b) => String(a).localeCompare(String(b)));
    let toBeLastColsKeyWords = ['classified', 'inbound']
    let lastCols = unique.filter((col) => toBeLastColsKeyWords.some((keyword) => col.toLowerCase().includes(keyword)));
    let firstCols = unique.filter((col) => !lastCols.includes(col));
    return [...firstCols, ...lastCols];
  }, [locationData]);

  const visibleRows = useMemo(
    () => (selectedRowKeys.length ? locationRows.filter((loc) => selectedRowKeys.includes(loc)) : locationRows),
    [locationRows, selectedRowKeys]
  );

  const dataByLocationMonth = useMemo(() => {
    const filtered = (locationData || [])
    const map = {};
    filtered.forEach((record) => {
      const loc = `${record.location} - ${BrandsTypes.find((b) => b.value === record.new_user_type)?.label || ""}`;
      const monthKey = getMonthKey(new Date(record.date * 1000));
      if (!map[loc]) map[loc] = {};
      if (!map[loc][monthKey]) map[loc][monthKey] = { ads: 0, spent: 0 };
      map[loc][monthKey].ads += Number(record.totalAds || 0);
      map[loc][monthKey].spent += Number(record.totalSpent || 0);
    });
    return map;
  }, [locationData]);

  const grandTotalsByMonth = useMemo(() => {
    const totals = {};
    monthColumns.forEach((m) => {
      const key = getMonthKey(m);
      totals[key] = { ads: 0, spent: 0 };
      visibleRows.forEach((loc) => {
        const cell = dataByLocationMonth[loc]?.[key];
        totals[key].ads += cell?.ads || 0;
        totals[key].spent += cell?.spent || 0;
      });
    });
    return totals;
  }, [monthColumns, visibleRows, dataByLocationMonth]);

  const colspan = 1 + monthColumns.length * 2;

  const handleDownloadCSV = () => {
    if (!locationRows.length || !monthColumns.length) return;
    const csvRows = locationRows.map((loc) => {
      const obj = { "Sales POC - District": formatLocationLabel(loc) };
      monthColumns.forEach((monthDate) => {
        const key = getMonthKey(monthDate);
        const cell = dataByLocationMonth[loc]?.[key] || { ads: 0, spent: 0 };
        obj[`${formatMonthHeader(monthDate)} - Count of Ads`] = cell.ads ?? "";
        obj[`${formatMonthHeader(monthDate)} - Revenue (Excl GST)`] = cell.spent ?? "";
      });
      return obj;
    });
    // Totals computed across the full locationRows (not visibleRows) so the export
    // always reflects every location, independent of the on-screen filter.
    const csvTotalsByMonth = {};
    monthColumns.forEach((m) => {
      const key = getMonthKey(m);
      csvTotalsByMonth[key] = { ads: 0, spent: 0 };
      locationRows.forEach((loc) => {
        const cell = dataByLocationMonth[loc]?.[key];
        csvTotalsByMonth[key].ads += cell?.ads || 0;
        csvTotalsByMonth[key].spent += cell?.spent || 0;
      });
    });
    const totalRow = {
      "Sales POC - District": "Grand Total",
      ...Object.fromEntries(
        monthColumns.flatMap((m) => {
          const key = getMonthKey(m);
          const t = csvTotalsByMonth[key] || { ads: 0, spent: 0 };
          return [
            [`${formatMonthHeader(m)} - Count of Ads`, t.ads ?? ""],
            [`${formatMonthHeader(m)} - Revenue (Excl GST)`, t.spent ?? ""],
          ];
        })
      ),
    };
    setCsvData([...csvRows, totalRow]);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current?.link?.click();
      setCsvData([]);
    }
  }, [csvData]);
  useEffect(() => {
    if (initiateDownloadCSV) {
      console.log("initiateDownloadCSV");
      handleDownloadCSV();
      setInitiateDownloadCSV(false);
    }
  }, [initiateDownloadCSV]);

  const csvFilename = `location_month_wise_report_${dateRange?.[0] ? new Date(dateRange[0]).toISOString().slice(0, 10) : "start"}_${dateRange?.[1] ? new Date(dateRange[1]).toISOString().slice(0, 10) : "end"}.csv`;

  return (
    <div className="alpha-report alpha-report-location-month-wise">
        <CSVLink data={csvData} filename={csvFilename} className="d-none" ref={csvLinkRef} />
      <MultiSelectFilter
        label="Locations"
        options={locationRows.map((loc) => ({ value: loc, label: formatLocationLabel(loc) }))}
        selected={selectedRowKeys}
        onChange={setSelectedRowKeys}
      />
      <div className="table-responsive alpha-report-table-wrap">
        <table className="table table-bordered table-sm align-middle alpha-report-table">
          <colgroup>
            <col style={{ width: "var(--sticky-width)" }} />
            {monthColumns.flatMap((m) => [
              <col key={`${getMonthKey(m)}-ads`} style={{ width: 70 }} />,
              <col key={`${getMonthKey(m)}-spent`} style={{ width: 70 }} />,
            ])}
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} className="alpha-report-th-sticky-first">
                Sales POC - District
              </th>
              {monthColumns.map((monthDate) => (
                <th key={getMonthKey(monthDate)} colSpan={2} className="text-center alpha-report-th-month" style={{ minWidth: 140 }}>
                  {formatMonthHeader(monthDate)}
                </th>
              ))}
            </tr>
            <tr>
              {monthColumns.map((monthDate) => (
                <React.Fragment key={`sub-${getMonthKey(monthDate)}`}>
                  <th className="alpha-report-th-sub" style={{ minWidth: 70 }}>Count of <br /> Ads</th>
                  <th className="alpha-report-th-sub" style={{ minWidth: 80 }}>Revenue <br />(Excl GST)</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          {loader ? (
            <TableLoader colspan={colspan} />
          ) : !visibleRows.length ? (
            <NoDataFound colspan={colspan} />
          ) : (
            <tbody>
              {visibleRows.map((loc) => (
                <tr key={loc}>
                  <td
                    title={formatLocationLabel(loc)}
                    className="alpha-report-td-sticky-first alpha-report-td-sticky-first-truncate"
                  >
                    {formatLocationLabel(loc)}
                  </td>
                  {monthColumns.map((monthDate) => {
                    const key = getMonthKey(monthDate);
                    const cell = dataByLocationMonth[loc]?.[key] || {
                      ads: 0,
                      spent: 0,
                    };
                    return (
                      <React.Fragment key={`${loc}-${key}`}>
                        <td className="text-center" style={{ minWidth: 70 }}>{cell.ads}</td>
                        <td style={{ minWidth: 70 }} title={cell.spent}>{cell.spent ? formatSpentValue(cell.spent) : ""}</td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
              <tr className="fw-bold">
                <td className="alpha-report-td-grand-total-sticky">Grand Total</td>
                {monthColumns.map((monthDate) => {
                  const key = getMonthKey(monthDate);
                  const t = grandTotalsByMonth[key] || { ads: 0, spent: 0 };
                  return (
                    <React.Fragment key={`total-${key}`}>
                      <td className="text-center alpha-report-td-grand-total">{t.ads}</td>
                      <td title={t.spent} className="alpha-report-td-grand-total">
                        {t.spent ? formatSpentValue(t.spent) : ""}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}

export default LocationMonthWiseReport;
